// Open Graph product tags (`product:*`) built from the variant the product page shows. Pure: no alias imports, so it is
// unit-tested with node --test. Moved here from chawk-bazar / ciseco (`helpers/productSeo.js` + `og_product_meta`):
// the backend builds these tags from the product's root price, which is 0 for a product with variants.
import { check_product_availability } from "../product/availability.js";
import { get_default_mix_variant } from "../product/selection.js";

// `web_currency` site setting (1 toman, 2 rial, 3 dollar) -> ISO 4217 code and price multiplier.
// Google does not know IRT (toman), so toman x 10 = IRR.
const CURRENCIES = {
  1: { currency: "IRR", multiplier: 10 },
  2: { currency: "IRR", multiplier: 1 },
  3: { currency: "USD", multiplier: 1 },
};

export const product_meta_currency = (web_currency) => CURRENCIES[parseInt(web_currency)] || CURRENCIES[2];

// The variant shown on load: the one carrying every `?selected-labels=` key (matched on `labels_obj`), otherwise the
// default combination (first available, else is_main_price, else first) — the same choice useProduct makes.
export const find_meta_variant = (mix_variant = [], selected_labels) => {
  const wanted = String(selected_labels || "")
    .split(",")
    .map((key) => key.trim())
    .filter(Boolean);

  if (wanted.length > 0) {
    const matched = mix_variant.find((item) => {
      const keys = Object.values(item?.labels_obj || {}).map((label) => label?.key);
      return wanted.every((key) => keys.includes(key));
    });
    if (matched) return matched;
  }

  return get_default_mix_variant({ mix_variant });
};

/**
 * @param {object} args
 * @param {Array} args.mix_variant       product.mix_variant (needs keys, labels_obj, price.{price,main_price}, details.count)
 * @param {string} [args.selected_labels] first value of `?selected-labels=`
 * @param {string|number} [args.web_currency]
 * @returns {object} `other` metadata entries
 */
export const build_product_og_meta = ({ mix_variant, selected_labels, web_currency } = {}) => {
  const variant = find_meta_variant(mix_variant || [], selected_labels);
  const { currency, multiplier } = product_meta_currency(web_currency);
  const to_iso = (value) => (Number(value) > 0 ? Number(value) * multiplier : 0);
  const price = to_iso(variant?.price?.main_price);
  const original_price = to_iso(variant?.price?.price);

  // A zero amount is not emitted (it reads as "free"); availability is always given.
  return {
    ...(price > 0 ? { "product:price:amount": String(price), "product:sale_price:amount": String(price) } : {}),
    ...(original_price > 0 ? { "product:original_price:amount": String(original_price) } : {}),
    ...(currency && (price > 0 || original_price > 0) ? { "product:price:currency": currency } : {}),
    "product:availability": check_product_availability(variant) ? "in stock" : "out of stock",
  };
};

// Price tags generate_metadata may have set from the SEO record; replaced as a group when variant data is available.
export const PRODUCT_PRICE_META_KEYS = [
  "product:price:amount",
  "product:sale_price:amount",
  "product:original_price:amount",
  "product:price:currency",
  "product:price.currency",
];
